import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import './BlogPostPage.css';
import { blogs, blogsByDate, getBlogById } from '../data/blogs';
import { renderRichText } from '../utils/richText';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const ContentBlock = ({ block }) => {
  switch (block.type) {
    case 'heading':
      return <h2 className="post-heading">{renderRichText(block.text)}</h2>;
    case 'subheading':
      return <h3 className="post-subheading">{renderRichText(block.text)}</h3>;
    case 'paragraph':
      return <p className="post-paragraph">{renderRichText(block.text)}</p>;
    case 'quote':
      return (
        <blockquote className="post-quote">
          <div className="post-quote-mark" aria-hidden="true">“</div>
          <p className="post-quote-text">{renderRichText(block.text)}</p>
          {block.cite && (
            <cite className="post-quote-cite">— {block.cite}</cite>
          )}
        </blockquote>
      );
    case 'verse':
      return (
        <div className="post-verse">
          <div className="post-verse-label">Quran</div>
          <p className="post-verse-text">{renderRichText(block.text)}</p>
          {block.cite && (
            <span className="post-verse-cite">{block.cite}</span>
          )}
        </div>
      );
    case 'hadith':
      return (
        <div className="post-hadith">
          <div className="post-hadith-label">Hadith</div>
          <p className="post-hadith-text">{renderRichText(block.text)}</p>
          {block.cite && (
            <span className="post-hadith-cite">{block.cite}</span>
          )}
        </div>
      );
    case 'list':
      return (
        <ul className="post-list">
          {block.items.map((item, idx) => (
            <li key={idx}>{renderRichText(item)}</li>
          ))}
        </ul>
      );
    case 'divider':
      return <div className="post-divider" aria-hidden="true" />;
    default:
      return null;
  }
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const blog = getBlogById(slug);
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const article = document.getElementById('post-article');
      if (!article) return;
      const rect = article.getBoundingClientRect();
      const articleTop = window.scrollY + rect.top;
      const articleHeight = article.offsetHeight;
      const windowH = window.innerHeight;
      const scrolled = window.scrollY - articleTop + windowH;
      const total = articleHeight;
      const pct = Math.max(0, Math.min(100, (scrolled / total) * 100));
      setProgress(pct);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [slug]);

  if (!blog) {
    return <Navigate to="/blog" replace />;
  }

  const related = blogsByDate.filter((b) => b.id !== blog.id).slice(0, 3);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: blog.title, text: blog.excerpt, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // user cancelled — no action
    }
  };

  return (
    <main className="blog-post-page">
      <div
        className="post-progress-bar"
        style={{ width: `${progress}%` }}
        aria-hidden="true"
      />

      <section
        className="post-hero"
        style={{ background: blog.coverGradient }}
      >
        <div className="post-hero-overlay" />
        <div className="container post-hero-inner">
          <Link to="/blog" className="post-back-link">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            All articles
          </Link>

          {blog.tags?.length > 0 && (
            <div className="post-hero-tags">
              {blog.tags.map((tag) => (
                <span key={tag} className="post-hero-tag">{tag}</span>
              ))}
            </div>
          )}

          <h1 className="post-hero-title">{blog.title}</h1>
          {blog.subtitle && (
            <p className="post-hero-subtitle">{blog.subtitle}</p>
          )}

          <div className="post-hero-meta">
            <span className="post-hero-author">By {blog.author}</span>
            <span className="post-hero-sep" aria-hidden="true">•</span>
            <span>{formatDate(blog.date)}</span>
            <span className="post-hero-sep" aria-hidden="true">•</span>
            <span>{blog.readTime} min read</span>
          </div>

          {blog.coverIcon && (
            <div className="post-hero-icon" aria-hidden="true">
              {blog.coverIcon}
            </div>
          )}
        </div>
      </section>

      <article id="post-article" className="post-article">
        <div className="container post-article-inner">
          {blog.content.map((block, idx) => (
            <ContentBlock key={idx} block={block} />
          ))}

          <div className="post-share">
            <span className="post-share-label">Share this article</span>
            <button
              type="button"
              className="post-share-btn"
              onClick={handleShare}
              aria-label="Share article"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              {copied ? 'Link copied!' : 'Share'}
            </button>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="post-related">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Continue Reading</h2>
              <div className="title-underline"></div>
            </div>

            <div className="post-related-grid">
              {related.map((b) => (
                <Link
                  key={b.id}
                  to={`/blog/${b.id}`}
                  className="post-related-card"
                  aria-label={`Read: ${b.title}`}
                >
                  <div
                    className="post-related-cover"
                    style={{ background: b.coverGradient }}
                  >
                    <span aria-hidden="true">{b.coverIcon}</span>
                  </div>
                  <div className="post-related-body">
                    <span className="post-related-meta">
                      {b.readTime} min read
                    </span>
                    <h3 className="post-related-title">{b.title}</h3>
                    <span className="post-related-author">By {b.author}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

// Eagerly import the blogs once so getBlogById is bound at module load
void blogs;

export default BlogPostPage;
